import { types, getEnv, flow, SnapshotOut, Instance } from 'mobx-state-tree';
import { EventDataStore, IEventDataStore } from './event';
import { IAppEnv } from '../appEnvironment';
import { ISportsBettingApi } from '../api/api';
import { AvailableGame } from './availableGame';
import { observable } from 'mobx';
import { flatten, groupBy, sum } from 'easit--utils';
import { gameDetailStore, createOdds, IgameDetailStore, tipList } from './gameDetail';
import { eventListWithDetailModel, eventDetailKeyModel } from './eventListWithDetailModel';
import { IGroupedApiCtx, ProgressStateModelForStore, contract, protoUtils, errorIsCancel, ProgressStateModel, rootCategoryId, IGetWebStandardEventsResponse, StandardEvent, VisualisationSubType, selectionVisualisationFlags2GameBusinessType, StatisticsType, StatisticsInApiStore, OddsPopularityStatsInApiStore, Timestamp, EBetGames } from 'eTip--common';
import { prepareFromTo, prepareRates, IParsedEventListParams } from '../routing';
import { gameGroupStore } from './gameGroup';
import { gameStore, gameAdvancedInfo } from './game';
import { getCategoryIdFromKey } from './mainCategoryMenu';
import { IRenameTipData, getRenameTipDataFromCompetitorNames, getCompetitorNamesFromEventName, canConvertTipName } from '../../eTipCommon/utils/eventsUtils';

export const EventLiveInPrematchListLivePrefixForUrl = "l";

export const GameDetailRowKey = 'gameDetailRow';
export const GameDetailRowStore = types.compose(
    types.model(GameDetailRowKey, {
        id: types.string, // as game.id
        eventId: types.number,
        categoryId: types.string,
        name: types.string,
        detail: gameDetailStore,
        IsDetails: types.boolean,
        BusinessType: types.number,
        gameInfo: types.maybeNull(types.string)
    }),
    gameAdvancedInfo
);

export const GameDetailRowGroupKey = 'gameDetailRowGroup';
export const GameDetailRowGroupStore = types.model(GameDetailRowGroupKey, {
    id: types.string, //as game.id
    gameName: types.string,
    gameInfo: types.maybeNull(types.string),
    details: types.optional(types.array(GameDetailRowStore), [])
});

export const LeagugeStoreKey = 'leagugeStore';
export const LeagugeStore = types
    .model(LeagugeStoreKey, {
        sportId: types.string,
        sportPresentationId: types.maybeNull(types.number),
        sportName: types.string,
        regionId: types.maybeNull(types.string),
        regionName: types.maybeNull(types.string),
        competitionId: types.maybeNull(types.string),
        competitionName: types.maybeNull(types.string),
        rowGroups: types.array(GameDetailRowGroupStore),
        originalCatIds: types.maybeNull(types.array(types.string)),
        specificLiveToShow: types.maybeNull(types.array(types.number))
    })
    .views(self => ({
        get id() {
            let key = `${self.sportId}`;
            if (!!self.regionId) {
                key = key.concat('_', `${self.regionId}`);
            }
            if (!!self.competitionId) {
                key = key.concat('_', `${self.competitionId}`);
            }
            return key;
        }
    }));

export const VirtualEventIdForLiveGame = -99;
export const VirtualEventIdForLiveGameAsString = "-99";
const VirtualDetailIdForLiveGame = -99;
const VirtualGameIdForZapasy = "-99";

export const categoryEventsStoreKey = 'categoryEventsStore';
export const CategoryEventsStore = types
    .compose(
        eventListWithDetailModel,
        types.model(categoryEventsStoreKey, {
            allGroupEventsCount: types.optional(types.number, 0),
            eventsMap: types.map(EventDataStore),
            availableGames: types.optional(types.array(AvailableGame), []),
            leagueRows: types.optional(types.array(types.string), []),
            leaguesMap: types.optional(types.map(LeagugeStore), {}),
            gamesFilterExpanded: types.optional(types.boolean, false),
            leagueRowsState: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
            nextLeagueRowsState: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
            categoryId: types.maybeNull(types.string),
            categoryKey: types.maybeNull(types.string),
            canShowLiveInPrematch: types.optional(types.boolean, false) // ! sets after first part of call loadAllAsync or loadLeaguesAsync !
        })
    )
    .views(self => {

        const rowsCount = (): number => {
            return sum(
                self.leagueRows.map(m => {
                    const league = self.leaguesMap.get(m);
                    return sum(league ? league.rowGroups.map(r => r.details.length) : []);
                })
            );
        };

        return {
            get rootCategory() {
                return rootCategoryId;
            },
            get defaultGameGroupId(): string | undefined {
                return self.availableGames.length ? self.availableGames[0].Id : undefined;
            },
            get hasOtherGames() {
                return self.availableGames.some(gf => !gf.Visible);
            },
            get loadingBatchCount() {
                return 50;
            },
            get rowsCount(): number {
                return rowsCount();
            },
            get canBeLoadedNextEvents() {
                return self.eventsMap.size < self.allGroupEventsCount;
            },

            getCouponTipData(id: number, gameName: string, eventName?: string) {
                const event = self.eventsMap.get(id.toString());
                return {
                    eventName: eventName || event.Name,
                    gameName: gameName
                };
            },

            visibleAvailableGames(activeGameId?: string) {
                return self.gamesFilterExpanded ? self.availableGames : self.availableGames.filter(game => game.Visible || activeGameId === game.Id);
            },

            listEventProviderForUpdate(_eventId: number): IEventDataStore {
                //return null to use listEventGameDetailProviderForUpdate
                return null;
            },

            listEventGameDetailProviderForUpdate(eventId: number): IgameDetailStore[] {
                let result: IgameDetailStore[] = [];
                let iterable = self.leaguesMap.values();
                let item = iterable.next();
                while (!item.done) {

                    let value: Instance<typeof LeagugeStore> = item.value;

                    //let result: Instance<typeof gameDetailStore> = null;
                    value.rowGroups.some(g => { //first in category finishes search in category
                        let found = g.details.find(groupDetail => groupDetail.eventId === eventId);
                        if (found) {
                            result.push(found.detail);
                            return true;
                        }
                        return false;
                    });

                    item = iterable.next();
                }

                return result;
            }
        };
    })
    .actions(self => {
        const env = getEnv<IAppEnv>(self);
        let synchroAllCtx: IGroupedApiCtx<ISportsBettingApi> = null;
        let synchroLeaguesCtx: IGroupedApiCtx<ISportsBettingApi> = null;

        /**
         * Loads list, at end also detail (synchronously, waiting for it to return).
         */
        const loadAllAsync: (params: IParsedEventListParams, withDefaults: boolean, favorites: number | null) => Promise<void> = flow(function* (params: IParsedEventListParams, withDefaults: boolean, favorites: number | null) {

            let ctx: IGroupedApiCtx<ISportsBettingApi> = null;
            try {
                if (synchroAllCtx && synchroAllCtx.isOpen()) {
                    synchroAllCtx.cancel('Load has been canceled.');
                }
                if (synchroLeaguesCtx && synchroLeaguesCtx.isOpen()) {
                    synchroLeaguesCtx.cancel('Load has been canceled.');
                }

                self.overallState = ProgressStateModel.Loading;
                self.leagueRowsState = ProgressStateModel.NotInitialized;
                self.resetDefaults();
                self.hideDetail();

                ctx = env.api.open();
                synchroAllCtx = ctx;
                const dateRange = prepareFromTo(params, env.dateTimeService);
                const rates = prepareRates(params);
                let canShowLiveInPrematch = getCanShowLiveInPrematch(dateRange, rates, params.gameId);
                self.canShowLiveInPrematch = canShowLiveInPrematch;

                const categoryId = getCategoryIdFromKey(params.categoryKey);

                const isFavorites = favorites > 0;
                const r: contract.IGetWebStandardEventsResponse =
                    isFavorites
                        ? yield ctx.api.getFavoriteEventsAsync({
                            from: dateRange.from,
                            to: dateRange.to,
                            rateFrom: rates.rateFrom,
                            rateTo: rates.rateTo,
                            searchBy: params.searchBy,
                            gameIds: params.gameId || params.gameId === EBetGames.Standard1X2 ? [params.gameId] : undefined,
                            top: self.loadingBatchCount,
                            includeLiveCategories: canShowLiveInPrematch
                        })
                        : yield ctx.api.getWebStandardEventsAsync({
                            categoryId: categoryId,
                            from: dateRange.from,
                            to: dateRange.to,
                            rateFrom: rates.rateFrom,
                            rateTo: rates.rateTo,
                            searchBy: params.searchBy,
                            gameIds: params.gameId || params.gameId === EBetGames.Standard1X2 ? [params.gameId] : undefined,
                            top: self.loadingBatchCount,
                            includeLiveCategories: canShowLiveInPrematch
                        });
                env.loggingService.info("load, getWebStandardEventsAsync", r);
                if (!!r) {
                    self.categoryId = categoryId;
                    self.categoryKey = params.categoryKey;
                    setAvailableGames(r.AvailableGames);
                    setClearedLeagues(r.EventTree, withDefaults, isFavorites);
                    self.allGroupEventsCount = r.UnpaginatedEventCount;
                }
                self.overallState = ProgressStateModel.Success;
                self.leagueRowsState = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in event list loading.', error);
                self.overallState = ProgressStateModel.Failed;
                self.leagueRowsState = ProgressStateModel.Failed;
            } finally {
                if (ctx === synchroAllCtx) {
                    synchroAllCtx = null;
                }
            }
        });

        /**
         * After user click on game in list. Load just leagues.
         */
        const loadLeaguesAsync = flow(function* (
            params: IParsedEventListParams,
            withDefaults: boolean,
            favorites: number
        ) {
            if (synchroAllCtx || self.overallState !== ProgressStateModel.Success) {
                return;
            }
            let ctx: IGroupedApiCtx<ISportsBettingApi> = null;
            try {
                if (synchroLeaguesCtx && synchroLeaguesCtx.isOpen()) {
                    synchroLeaguesCtx.cancel('Load has been canceled.');
                }

                self.leagueRowsState = ProgressStateModel.Loading;

                self.resetDefaults();
                self.hideDetail();
                ctx = env.api.open();
                synchroLeaguesCtx = ctx;

                const dateRange = prepareFromTo(params, env.dateTimeService);
                const rates = prepareRates(params);
                let canShowLiveInPrematch = getCanShowLiveInPrematch(dateRange, rates, params.gameId);
                self.canShowLiveInPrematch = canShowLiveInPrematch;

                const isFavorites = favorites > 0;
                const r: IGetWebStandardEventsResponse | null =
                    isFavorites
                        ? yield ctx.api.getFavoriteEventsAsync({
                            from: dateRange.from,
                            to: dateRange.to,
                            rateFrom: rates.rateFrom,
                            rateTo: rates.rateTo,
                            searchBy: params.searchBy,
                            gameIds: params.gameId || (!__IsMTS__ && params.gameId === EBetGames.Standard1X2) ? [params.gameId] : undefined,
                            top: self.loadingBatchCount,
                            includeLiveCategories: canShowLiveInPrematch
                        })
                        : yield ctx.api.getWebStandardEventsAsync({
                            categoryId: getCategoryIdFromKey(params.categoryKey),
                            from: dateRange.from,
                            to: dateRange.to,
                            rateFrom: rates.rateFrom,
                            rateTo: rates.rateTo,
                            searchBy: params.searchBy,
                            gameIds: params.gameId || (!__IsMTS__ && params.gameId === EBetGames.Standard1X2) ? [params.gameId] : undefined,
                            top: self.loadingBatchCount,
                            includeLiveCategories: canShowLiveInPrematch
                        });
                env.loggingService.info("loadGame, getWebStandardEventsAsync", r);
                if (!!r) {
                    setClearedLeagues(r.EventTree, withDefaults, isFavorites);
                    self.allGroupEventsCount = r.UnpaginatedEventCount;

                }
                self.leagueRowsState = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in event list game loading.', error);
                self.leagueRowsState = ProgressStateModel.Failed;
            } finally {
                if (synchroLeaguesCtx === ctx) {
                    synchroLeaguesCtx = null;
                }
            }
        });

        /**
         * Load automatically after user scrolls down, is subordinate of loadLeagues.
         */
        const loadNextLeaguesAsync = flow(function* (
            params: {
                categoryKey?: string;
                gameId?: string;
                searchBy?: string;
                eventId?: number;
                detailId?: number;
            },
            _lastPosition: number,
            favorites: number
        ) {
            if (self.categoryKey !== params.categoryKey || synchroLeaguesCtx /* there is another request, either master load leagues or another load next leagues. There is no reason to cancel it and start again */) {
                return;
            }
            self.nextLeagueRowsState = ProgressStateModel.Loading;
            let ctx: IGroupedApiCtx<ISportsBettingApi> = null;
            try {
                ctx = env.api.open();
                synchroLeaguesCtx = ctx;
                const dateRange = prepareFromTo(params, env.dateTimeService);
                const rates = prepareRates(params);
                const isFavorites = favorites > 0;
                const r: IGetWebStandardEventsResponse | null =
                    isFavorites
                        ? yield ctx.api.getFavoriteEventsAsync({
                            from: dateRange.from,
                            to: dateRange.to,
                            rateFrom: rates.rateFrom,
                            rateTo: rates.rateTo,
                            searchBy: params.searchBy,
                            gameIds: params.gameId || params.gameId === EBetGames.Standard1X2 ? [params.gameId] : undefined,
                            top: self.loadingBatchCount,
                            skip: self.rowsCount,
                            includeLiveCategories: getCanShowLiveInPrematch(dateRange, rates, params.gameId)
                        })
                        : yield ctx.api.getWebStandardEventsAsync({
                            categoryId: getCategoryIdFromKey(params.categoryKey),
                            from: dateRange.from,
                            to: dateRange.to,
                            rateFrom: rates.rateFrom,
                            rateTo: rates.rateTo,
                            searchBy: params.searchBy,
                            gameIds: params.gameId || params.gameId === EBetGames.Standard1X2 ? [params.gameId] : undefined,
                            top: self.loadingBatchCount,
                            skip: self.rowsCount,
                            includeLiveCategories: getCanShowLiveInPrematch(dateRange, rates, params.gameId)
                        });
                env.loggingService.info("loadNext, getWebStandardEventsAsync", r);
                if (!!r) {
                    addLeagues(r.EventTree, isFavorites);
                    self.allGroupEventsCount = r.UnpaginatedEventCount;
                }
                self.nextLeagueRowsState = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in event list loading.', error);
                self.nextLeagueRowsState = ProgressStateModel.Failed;
            } finally {
                if (ctx === synchroLeaguesCtx) {
                    synchroLeaguesCtx = null;
                }
            }
        });

        const toggleGamesFilter = () => {
            self.gamesFilterExpanded = !self.gamesFilterExpanded;
        };

        const setClearedLeagues = (items: contract.IFirstLevelCategory, withDefaults: boolean, canProcessSpecificLiveEventsToShow: boolean) => {
            self.eventsMap.clear();
            self.leaguesMap.clear();
            self.allGroupEventsCount = 0;
            if (withDefaults) {
                delete self.defaults;
            }
            self.leagueRows = observable(createLeagueRows(items, withDefaults, canProcessSpecificLiveEventsToShow));
        };

        const addLeagues = (items: contract.IFirstLevelCategory, canProcessSpecificLiveEventsToShow: boolean) => {
            const affectedRows = createLeagueRows(items, false, canProcessSpecificLiveEventsToShow);
            const newRows = affectedRows.filter(n => !self.leagueRows.filter(l => l === n).length);
            self.leagueRows.push(...newRows);
        };

        const processEvent = (
            e: contract.IEvent,
            eventIdx: number,
            sportIdx: number, //these are indexes in list!
            regionIdx: number,
            competitionIdx: number,
            withDefaults: boolean,
            vmtKeys: string[] = []
        ) => {
            if (e.Id == VirtualEventIdForLiveGame) {
                e.Date = Timestamp.create({ Value: 1 });
            }

            let groups = e.GameGroups.map(group => {
                //!!only important data is loaded now (skipping properties and all odds)
                return gameGroupStore.create(<SnapshotOut<typeof gameGroupStore>>{
                    ID: group.ID,
                    Games: group.Games.map(game => {
                        if (e.Id == VirtualEventIdForLiveGame) {
                            game.Details = [{ ID: VirtualDetailIdForLiveGame }];
                        };
                        return gameStore.create(<SnapshotOut<typeof gameStore>>{
                            ID: game.ID,
                            BusinessType: game.BusinessType,
                            IsDetails: game.IsDetails,
                            Sequence: game.Sequence,
                            Details: game.Details.map(detail => {
                                return gameDetailStore.create(<SnapshotOut<typeof gameDetailStore>>{
                                    ID: detail.ID,
                                    LineNumber: detail.LineNumber
                                });
                            }),

                            CombCountMin: null,
                            CombRateMin: null,
                            Info: null,
                            Name: null,
                            StakeMax: null,
                            System: null,
                        });
                    }),
                    Name: null
                });
            });

            self.eventsMap.set(
                `${e.Id}`,
                EventDataStore.create(<SnapshotOut<typeof EventDataStore>>
                    {
                        Id: e.Id,
                        CategoryId: e.CategoryId,
                        Name: e.Name,
                        Date: protoUtils.protoTimeStamp2Date(e.Date).getTime(),
                        DetailsCount: e.DetailCount,
                        GameGroups: observable(groups),
                        Statistics: e.StatisticsExternalId?.length ? StatisticsInApiStore.create(<SnapshotOut<typeof StatisticsInApiStore>>{ id: e.StatisticsExternalId, type: StatisticsType.BRMatch }) : null,
                        Info: e.Info,
                        MarketingMessage: e.MarketingMessage,
                        EventTvs:
                            e.Tvs && e.Tvs.length
                                ? observable(
                                    e.Tvs.map(tv => {
                                        return { id: tv.Id, name: tv.Name, date: protoUtils.protoTimeStamp2Date(tv.Date).getTime() };
                                    })
                                )
                                : null,

                        CategoryPath: null,
                        GraphicsPresentationID: null,
                        ShowDayOfDate: null,
                        remainingSeconds: null,
                        SuperOddsPopularityStats: observable(e.SuperOddsPopularityStats.map(x => OddsPopularityStatsInApiStore.create(<SnapshotOut<typeof OddsPopularityStatsInApiStore>>{ tipId: x.TipID, name: x.Name, percentage: x.Percentage })))
                    },
                    env
                )
            );

            let renameTipData: IRenameTipData;
            if (__IsMTS__ && !e.IsVmtCompetitor) {
                renameTipData = getRenameTipDataFromCompetitorNames(getCompetitorNamesFromEventName(e.Name));
            }

            return flatten(
                e.GameGroups.map((group, groupIdx) =>
                    flatten(
                        group.Games.map((game, gameIdx) => {

                            if (game.Details.length == 1 && game.Details[0].ID == VirtualDetailIdForLiveGame) {
                                let gameDetail = game.Details[0];
                                return [GameDetailRowStore.create(<SnapshotOut<typeof GameDetailRowStore>>{
                                    id: createGameDetailRowId(game, group),
                                    eventId: e.Id,
                                    categoryId: e.CategoryId,
                                    IsDetails: game.IsDetails,
                                    BusinessType: -1,
                                    name: game.Name,
                                    detail: gameDetailStore.create(<SnapshotOut<typeof gameDetailStore>>{
                                        ID: gameDetail.ID,
                                        Name: gameDetail.Name,
                                        LineNumber: gameDetail.LineNumber,
                                        Odds: tipList.create()
                                    }),
                                    CombRateMin: gameDetail.CombRateMin,
                                    CombCountMin: gameDetail.CombCountMin,
                                    System: gameDetail.CanBePlayedAsSystemBet,
                                    StakeMax: gameDetail.StakeMax,
                                    gameInfo: game.Info
                                })];
                            }

                            let gameTipRenameData = canConvertTipName(game) ? renameTipData : null;

                            return game.Details.map((gameDetail, gameDetailIdx) => {
                                if (withDefaults && !sportIdx && !regionIdx && !competitionIdx && !eventIdx && !groupIdx && !gameIdx && !gameDetailIdx) {
                                    delete self.defaults;
                                    self.defaults = eventDetailKeyModel.create(<SnapshotOut<typeof eventDetailKeyModel>>{
                                        eventId: e.Id,
                                        detailId: gameDetail.ID,
                                        categoryId: e.CategoryId,
                                        isLive: false
                                    });
                                }

                                let canRenameTips: boolean = false;

                                let odds: StandardEvent.OddsListItem[] = [];
                                if (!e.IsVmtCompetitor) { //for !__IsMts  only (mts has always IsVmtCompetitor false)
                                    canRenameTips = __IsMTS__;
                                    gameDetail.OddsList.forEach((odd, idx) => {
                                        if (!__IsMTS__ && idx === 0 && odd.TipID === "2" && game.ID === EBetGames.Standard12) {
                                            //add placeholder for 12 games
                                            odds.push({
                                                DetailID: gameDetail.ID,
                                                EventID: e.Id,
                                                GameID: game.ID,
                                                Name: odd.Name,
                                                PopularityPercentage: 0,
                                                Rate: 1,
                                                TipID: odd.TipID,
                                                State: odd.State
                                            });
                                        }
                                        odds.push({
                                            DetailID: gameDetail.ID,
                                            EventID: e.Id,
                                            GameID: game.ID,
                                            Name: odd.Name,
                                            PopularityPercentage: 0,
                                            Rate: odd.Rate,
                                            TipID: odd.TipID,
                                            State: odd.State
                                        });
                                    });
                                } else {
                                    odds = vmtKeys.map(vmtKey => {
                                        const idx = gameDetail.OddsList.findIndex(oddItem => oddItem.Name === vmtKey);
                                        if (idx > -1) {
                                            const odd = gameDetail.OddsList[idx];
                                            return {
                                                DetailID: gameDetail.ID,
                                                EventID: e.Id,
                                                GameID: game.ID,
                                                Name: odd.Name,
                                                OrigName: odd.Name,
                                                PopularityPercentage: 0,
                                                Rate: odd.Rate,
                                                TipID: odd.TipID,
                                                State: odd.State
                                            };
                                        } else {
                                            return {
                                                DetailID: 0,
                                                EventID: 0,
                                                GameID: "",
                                                Name: vmtKey,
                                                OrigName: vmtKey,
                                                PopularityPercentage: 0,
                                                Rate: 1,
                                                TipID: '',
                                                State: null
                                            };
                                        }
                                    });
                                }

                                let gameBusinessType = __IsMTS__ ? selectionVisualisationFlags2GameBusinessType(game.SelectionVisualisationFlags) : game.BusinessType;

                                return GameDetailRowStore.create(<SnapshotOut<typeof GameDetailRowStore>>{
                                    id: createGameDetailRowId(game, group),
                                    eventId: e.Id,
                                    categoryId: e.CategoryId,
                                    IsDetails: game.IsDetails,
                                    BusinessType: gameBusinessType,
                                    name: game.Name,
                                    detail: gameDetailStore.create(<SnapshotOut<typeof gameDetailStore>>{
                                        ID: gameDetail.ID,
                                        Name: gameDetail.Name,
                                        LineNumber: gameDetail.LineNumber,
                                        Odds: createOdds(odds, gameBusinessType, gameDetail.Name, game.ID, canRenameTips ? gameTipRenameData : null)
                                    }),
                                    CombRateMin: gameDetail.CombRateMin,
                                    CombCountMin: gameDetail.CombCountMin,
                                    System: gameDetail.CanBePlayedAsSystemBet,
                                    StakeMax: gameDetail.StakeMax,
                                    gameInfo: game.Info
                                });
                            })
                        })
                    )
                )
            );
        };

        const addLeagueRow = (
            key: string, //compound of categories
            groupedDetailsById: Map<string, SnapshotOut<typeof GameDetailRowStore>[]>, //{ [id: string /* createGameDetailRowId */]: SnapshotOut<typeof GameDetailRowStore>[] },
            sport: contract.ISecondLevelCategory,
            region: contract.ICategoryBase | null,
            competition: contract.ICategoryBase | null,
            canProcessSpecificLiveEventsToShow: boolean
        ): void => {
            if (!self.leaguesMap.has(key)) {
                const rowGroups: SnapshotOut<typeof GameDetailRowGroupStore>[] = [];

                // Object.keys(groupedDetailsById).map(k => {
                groupedDetailsById.forEach((details, k) => {
                    // same below start
                    let gameName = details.length ? details[0].name : 'Unknown';
                    let gameInfo = details.length ? details[0].gameInfo : null;
                    let index = details.findIndex(d => d.detail.ID === VirtualDetailIdForLiveGame);
                    if (index >= 0) {
                        details.splice(index, 1);
                    }
                    // same below end
                    rowGroups.push(GameDetailRowGroupStore.create(<SnapshotOut<typeof GameDetailRowGroupStore>>{
                        id: k,
                        gameName: gameName,
                        details: observable(details),
                        gameInfo: gameInfo
                    })
                    );
                });

                let originalCatIds: string[] = [];
                let specificLiveToShow: number[];
                if (canProcessSpecificLiveEventsToShow) {
                    specificLiveToShow = [];
                }
                if (sport.Base) {
                    if (sport.Base.OriginalCategories) {
                        originalCatIds = originalCatIds.concat(sport.Base.OriginalCategories);
                    }
                    if (canProcessSpecificLiveEventsToShow && sport.Base.SpecificLiveEventsToShow) {
                        specificLiveToShow = specificLiveToShow.concat(sport.Base.SpecificLiveEventsToShow);
                    }
                }
                if (region) {
                    if (region.OriginalCategories) {
                        originalCatIds = originalCatIds.concat(region.OriginalCategories);
                    }
                    if (canProcessSpecificLiveEventsToShow && region.SpecificLiveEventsToShow) {
                        specificLiveToShow = specificLiveToShow.concat(region.SpecificLiveEventsToShow);
                    }
                }
                if (competition) {
                    if (competition.OriginalCategories) {
                        originalCatIds = originalCatIds.concat(competition.OriginalCategories);
                    }
                    if (canProcessSpecificLiveEventsToShow && competition.SpecificLiveEventsToShow) {
                        specificLiveToShow = specificLiveToShow.concat(competition.SpecificLiveEventsToShow);
                    }
                }



                const league = LeagugeStore.create(<SnapshotOut<typeof LeagugeStore>>{
                    sportId: sport.Base.Id,
                    sportName: sport.Base.Name,
                    sportPresentationId: sport.GraphicsPresentationId,
                    regionId: region && region.Id,
                    regionName: region && region.Name,
                    competitionId: competition && competition.Id,
                    competitionName: competition && competition.Name,
                    rowGroups: observable(rowGroups),
                    originalCatIds: originalCatIds.length ? originalCatIds : null,
                    specificLiveToShow: specificLiveToShow && specificLiveToShow.length ? specificLiveToShow : null
                });
                self.leaguesMap.set(league.id, league);
            } else {
                const existingLeauge = self.leaguesMap.get(key);
                groupedDetailsById.forEach((details, k) => {
                    // same above start
                    let gameName = details.length ? details[0].name : 'Unknown';
                    let gameInfo = details.length ? details[0].gameInfo : null;
                    let index = details.findIndex(d => d.detail.ID === VirtualDetailIdForLiveGame);
                    if (index >= 0) {
                        details.splice(index, 1);
                    }
                    // same above end
                    const foundRowGroups = existingLeauge.rowGroups.filter(rg => rg.id === k);
                    if (foundRowGroups.length)
                        details.forEach((nd: SnapshotOut<typeof GameDetailRowStore>) => {
                            if (!foundRowGroups[0].details.some(d => nd.eventId == d.eventId && nd.detail.ID === d.detail.ID)) { //changed together with game.id number to string (UOF-608) as it compared game.id with detail.id
                                //env.loggingService.info("adding ", nd);
                                foundRowGroups[0].details.push(nd);
                                return;
                            }
                            //throw `Error: found the same detail. ${nd.id}`;
                        });
                    else {
                        existingLeauge.rowGroups.push(
                            GameDetailRowGroupStore.create(<SnapshotOut<typeof GameDetailRowGroupStore>>{
                                id: k,
                                gameName: gameName,
                                details: observable(details),
                                gameInfo: gameInfo
                            })
                        );
                    }
                });
            }
        };

        const createLeagueRows = (items: contract.IFirstLevelCategory, withDefaults: boolean, canProcessSpecificLiveEventsToShow: boolean): string[] => {
            const getVMTCols = (events: contract.IEvent[]): string[] => {
                let keys: { [key: string]: string | null } = {};
                events.forEach(event => {
                    if (event.IsVmtCompetitor) {
                        event.GameGroups.forEach(gameGroups => {
                            gameGroups.Games.forEach(game => {
                                game.Details.forEach(detail => {
                                    detail.OddsList.forEach(odd => {
                                        keys[odd.Name] = odd.TipID;
                                    });
                                });
                            });
                        });
                    }
                });
                return Object.keys(keys).sort((a, b) => {
                    return keys[a] > keys[b] ? 1 : -1;
                });
            };

            return flatten(
                items.Categories.map((s, sportIdx) => {
                    let ret: string[] = [];
                    if (!!s.Categories && !!s.Categories.length) {
                        ret = ret.concat(
                            flatten(
                                s.Categories.map((r, regionIdx) => {
                                    let regionRet: string[] = [];

                                    if (!!r.Categories && !!r.Categories.length) {
                                        regionRet = regionRet.concat(
                                            r.Categories.map((co, competitionIdx) => {
                                                const vmtKeys = getVMTCols(co.Base.Events);
                                                const detailsOfAllEvents = flatten(
                                                    co.Base.Events.map((e, eventIdx) => {
                                                        return processEvent(e, eventIdx, sportIdx, regionIdx, competitionIdx, withDefaults, vmtKeys);
                                                    })
                                                );
                                                const groupedDetailsById = groupBy(detailsOfAllEvents, 'id'); //expecting keys has same order as inserted
                                                const id = `${s.Base.Id}_${r.Base.Id}_${co.Base.Id}`;
                                                addLeagueRow(id, groupedDetailsById, s, r.Base, co.Base, canProcessSpecificLiveEventsToShow);
                                                return id;
                                            })
                                        );
                                    }

                                    if (!!r.Base.Events.length) {
                                        const vmtKeys = getVMTCols(r.Base.Events);
                                        const detailsOfAllEvents = flatten(
                                            r.Base.Events.map((e, eventIdx) => {
                                                return processEvent(e, eventIdx, sportIdx, regionIdx, 1, withDefaults, vmtKeys);
                                            })
                                        );
                                        const groupedDetailsById = groupBy(detailsOfAllEvents, 'id'); //expecting keys has same order as inserted
                                        const id = `${s.Base.Id}_${r.Base.Id}`;
                                        addLeagueRow(id, groupedDetailsById, s, r.Base, null, canProcessSpecificLiveEventsToShow);
                                        regionRet = regionRet.concat([id]);
                                    }

                                    return regionRet;
                                })
                            )
                        );
                    }

                    if (!!s.Base.Events.length) {
                        const vmtKeys = getVMTCols(s.Base.Events);
                        const detailsOfAllEvents = flatten(
                            s.Base.Events.map((e, eventIdx) => {
                                return processEvent(e, eventIdx, sportIdx, 1, 1, withDefaults, vmtKeys);
                            })
                        );
                        const groupedDetailsById = groupBy(detailsOfAllEvents, 'id');
                        addLeagueRow(`${s.Base.Id}`, groupedDetailsById, s, null, null, canProcessSpecificLiveEventsToShow);
                        ret = ret.concat([`${s.Base.Id}`]);
                    }
                    return ret;
                })
            );
        };

        const gameNotAggregatedRx = new RegExp("^([0-9]+)d([0-9]+)$");

        const createGameDetailRowId = (game: contract.IGame, gameGroup: contract.IGameGroup): string => {
            if (game.VisualisationSubType === VisualisationSubType.MergeMarketTypeHeadersBetweenEvents) {
                let match = gameNotAggregatedRx.exec(game.ID);
                if (match) {
                    return `gd${match[1]}`;  // for NotAggregated with compound id <markettype>d<detailId> use markettype only (detailId is a event specific)
                }
                return `gg${gameGroup.ID}`;
            }
            return game.ID;
        }

        const setAvailableGames = (games: contract.IGameFilterItem[]) => {
            self.availableGames = observable(games.map(g => AvailableGame.create(
                <SnapshotOut<typeof AvailableGame>>{ Id: g.Id == null ? null : String(g.Id), Name: g.Name, Visible: g.Visible }
            )));
        };

        const removeLiveEvents = (evtIdsInLive: Set<number>) => {
            self.leagueRows.forEach(rid => {
                let league = self.leaguesMap.get(rid);
                if (!league) {
                    return;
                }
                league.rowGroups.forEach(rg => {
                    let toRemove: Instance<typeof GameDetailRowStore>[];
                    rg.details.forEach(d => {
                        if (evtIdsInLive.has(d.eventId)) {
                            if (!toRemove) {
                                toRemove = [];
                            }
                            toRemove.push(d);
                        }
                    });
                    toRemove?.forEach(d => {
                        rg.details.remove(d);
                    });
                });
            });
        };

        const getCanShowLiveInPrematch = (preparedDateRange: { canUseLiveInPrematch: boolean }, preparedRates: { rateFrom?: number, rateTo?: number }, gameId: string | null): boolean => {
            return getCanShowLiveInPrematchCommon(env.settings.canShowLiveInPrematch, preparedDateRange, preparedRates, gameId);
        }

        return {
            loadAllAsync,
            loadLeaguesAsync,
            loadNextLeaguesAsync,
            toggleGamesFilter,
            removeLiveEvents
        };
    });

export const getCanShowLiveInPrematchCommon = (canShowLiveInPrematchFromSettings: boolean, preparedDateRange: { canUseLiveInPrematch: boolean }, preparedRates: { rateFrom?: number, rateTo?: number }, gameId: string | null): boolean => {
    return canShowLiveInPrematchFromSettings && preparedDateRange.canUseLiveInPrematch && (!preparedRates.rateFrom && !preparedRates.rateTo) && (gameId === VirtualGameIdForZapasy || !gameId);
}